import { removeMdnFormatting } from "@expert/common-ui";
import { type CountryCode, parsePhoneNumberFromString } from "libphonenumber-js";

import { env as configEnv } from "@/config";

import { DEFAULT_COUNTRY_CODE } from "./validatePhoneNumber";

export type MdnClipboardFormat = "auto" | "e164Digits" | "e164WithPlus" | "national";

export interface GetMdnClipboardOptions {
    format?: MdnClipboardFormat;
    defaultCountry?: CountryCode;
    /** Strip the country code from NANP numbers. Defaults to `true`. */
    removeCountryCode?: boolean;
}

/**
 * Normalizes an MDN for clipboard paste (carrier tools, CRM, etc.).
 *
 * Default (`format: "auto"`): NANP (country calling code 1) → 10-digit national significant number;
 * other regions → E.164 digits without a leading `+`.
 */
export function getMdnClipboardValue(
    mdn: string,
    { format = "auto", defaultCountry = DEFAULT_COUNTRY_CODE, removeCountryCode = true }: GetMdnClipboardOptions = {},
): string {
    const normalized = removeMdnFormatting(mdn).trim();
    if (!normalized) {
        return "";
    }

    const parsed = normalized.startsWith("+")
        ? parsePhoneNumberFromString(normalized)
        : parsePhoneNumberFromString(normalized, { defaultCountry });

    const isAcceptable = parsed ? (configEnv.isProdMode ? parsed.isValid() : parsed.isPossible()) : false;

    if (parsed && isAcceptable) {
        switch (format) {
            case "e164WithPlus":
                return parsed.format("E.164");
            case "national":
                return parsed.formatNational();
            case "e164Digits":
                return parsed.format("E.164").replace(/^\+/, "");
            case "auto":
            default:
                // TODO: When expanding to non-NANP regions, decide whether `auto` should
                // return `parsed.nationalNumber` for all countries or keep E.164 digits.
                if (removeCountryCode && parsed.countryCallingCode === "1") {
                    return parsed.nationalNumber;
                }
                return parsed.format("E.164").replace(/^\+/, "");
        }
    }

    return fallbackClipboardFromUnparseable(normalized, format, removeCountryCode);
}

function fallbackClipboardFromUnparseable(
    normalized: string,
    format: MdnClipboardFormat,
    removeCountryCode: boolean,
): string {
    const digitsOnly = normalized.replace(/\D/g, "");
    if (!digitsOnly) {
        return normalized;
    }

    switch (format) {
        case "e164WithPlus":
            return `+${digitsOnly}`;
        case "national":
        case "e164Digits":
            return digitsOnly;
        case "auto":
        default:
            if (removeCountryCode && digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
                return digitsOnly.slice(1);
            }
            return digitsOnly;
    }
}
